import GridTile from "../../grid/GridTile";
import Path from "../Path";
import PathPlan from "../plan/PathPlan";
import PathTemplatePattern from "./PathTemplatePattern";
import { Template } from "./Template";


export default class PathTemplate {
    constructor() {
        /**
         * 2D Array used for pattern matching. This is a simplified representation of the path and terrain.
         * @private
         * @type {Array.<Array.<number>>}
         */
        this.template = this.initializeTemplate(30, 30);

        /**
         * @type {number}
         */
        this.startColIndex = 0;
        /**
         * @type {number}
         */
        this.startRowIndex = 0;
    }

    /**
     * @param {Path} path 
     * @param {boolean} tileHealthNegligible
     */
    updateTemplate(path, tileHealthNegligible) {
        this.buildTemplate(path, tileHealthNegligible); // has side effect of setting start col/row properties.
    }

    /**
     * @param {PathTemplatePattern} pattern
     * @param {Path} path 
     * @param {number} pathIndex The path index.
     * @returns {PathPlan}
     */
    getPlanIfPatternMatch(pattern, path, pathIndex) {
        const curr = path.tiles[pathIndex];
        const currCol = curr.getTileCol();
        const currRow = curr.getTileRow();

        const start = path.tiles[0];
        const startCol = start.getTileCol();
        const startRow = start.getTileRow();

        const templateCol = this.startColIndex + (currCol - startCol);
        const templateRow = this.startRowIndex + (currRow - startRow);
        
        return pattern.getPlanIfMatch(path, pathIndex, templateCol, templateRow, this.template);
    }

    /**
     * @private
     * @param {Path} path 
     * @param {boolean} tileHealthNegligible
     */
    buildTemplate(path, tileHealthNegligible) {

        // We need at least 2 steps in the path.
        if (path.size < 2) {
            return;
        }

        // find x-range, in units of tile col
        // find y-range, in units of tile row

        const step0 = path.tiles[0];
        let minCol = step0.getTileCol();
        let maxCol = minCol;
        let minRow = step0.getTileRow();
        let maxRow = minRow;

        for (let i = 1; i < path.size; i++) {
            const step = path.tiles[i];
            const col = step.getTileCol();
            const row = step.getTileRow();

            minCol = Math.min(minCol, col);
            maxCol = Math.max(maxCol, col);

            minRow = Math.min(minRow, row);
            maxRow = Math.max(maxRow, row);
        }

        // pad the min/max ranges by 1 
        minCol--;
        minRow--;
        maxCol += 2;
        maxRow += 2;

        const deltaCol = maxCol - minCol;
        const deltaRow = maxRow - minRow;

        // Make sure the template is large enough for the curren path. 
        // Clear out any old stuff from previous usages.
        this.growAndClearTemplate(deltaCol, deltaRow);

        // Location of start tile in the template:
        const startTile = path.tiles[0];
        this.startColIndex = startTile.getTileCol() - minCol;
        this.startRowIndex = startTile.getTileRow() - minRow;

        let col = this.startColIndex;
        let row = this.startRowIndex;

        if (tileHealthNegligible) {
            this.populateNegligibleTileAndNeighbors(startTile, col, row, 0);
        }
        else {
            this.populateTileAndNeighbors(startTile, col, row, 0);
        }

        let prev = startTile;
        for (let i = 1; i < path.size; i++) {
            const curr = path.tiles[i];

            col += (curr.getTileCol() - prev.getTileCol());
            row += (curr.getTileRow() - prev.getTileRow());

            if (tileHealthNegligible) {
                this.populateNegligibleTileAndNeighbors(curr, col, row, i);
            }
            else {
                this.populateTileAndNeighbors(curr, col, row, i);
            }

            prev = curr;
        }
    }

    /**
     * @private
     * @param {number} cols 
     * @param {number} rows 
     * @returns {Array.<number>}
     */
    initializeTemplate(cols, rows) {
        const template = new Array(cols);

        for (let col = 0; col < cols; col++) {
            const rowArray = new Array(rows);
            rowArray.fill(Template.IGNORE);
            template[col] = rowArray;
        }

        return template;
    }

    /**
     * @private
     * @param {number} newCols
     * @param {number} newRows
     */
    growAndClearTemplate(newCols, newRows) {

        const currentCols = this.template.length;
        const currentRows = this.template[0].length;

        let grew = false;

        if (currentRows < newRows) {
            // Update the length of all the existing rows
            for (let col = 0; col < this.template.length; col++) {
                const rowArray = this.template[col];
                const currLen = rowArray.length;
                
                rowArray.length = newRows; // <-- changes the array length
    
                // Fill new entries with IGNORE
                for (let row = currLen; row < newRows; row++) {
                    rowArray[row] = Template.IGNORE;
                }

            }
            grew = true;
        }

        // Add new columns
        if (currentCols < newCols) {
            this.template.length = newCols; // <-- changes the array length

            // The number of rows may or may not have changed.
            const rowSize = Math.max(currentRows, newRows);

            for (let col = currentCols; col < newCols; col++) {
                const rowArray = new Array(rowSize);
                for (let row = 0; row < rowSize; row++) {
                    rowArray[row] = Template.IGNORE;
                }
                this.template[col] = rowArray;
            }
            grew = true;            
        }

        // We need to clear out the contents of the template
        for (let col = 0; col < this.template.length; col++) {
            const rowArray = this.template[col];
            rowArray.fill(Template.IGNORE);
        }

        if (grew) {
            console.log('PathTemplate grew. [' + this.template.length + '][' + this.template[0].length + ']');
        }
    }

    
    /**
     * @private
     * @param {GridTile} tile 
     * @param {number} col 
     * @param {number} row 
     * @param {number} pathIndex
     */
    populateTileAndNeighbors(tile, col, row, pathIndex) {

        if (tile.open) {
            this.template[col][row] = pathIndex;
        }
        else {
            this.template[col][row] = Template.CLOSED;
        }

        // Top
        if (this.template[col][row - 1] === Template.IGNORE) {
            const neighbor = tile.getNeighborTop();
            if (neighbor) {
                this.template[col][row - 1] = neighbor.open ? Template.OPEN : Template.CLOSED;
            }
        }

        // Below
        if (this.template[col][row + 1] === Template.IGNORE) {
            const neighbor = tile.getNeighborBottom();
            if (neighbor) {
                this.template[col][row + 1] = neighbor.open ? Template.OPEN : Template.CLOSED;
            }
        }

        // Left
        if (this.template[col - 1][row] === Template.IGNORE) {
            const neighbor = tile.getNeighborLeft();
            if (neighbor) {
                this.template[col - 1][row] = neighbor.open ? Template.OPEN : Template.CLOSED;
            }
        }

        // Right
        if (this.template[col + 1][row] === Template.IGNORE) {
            const neighbor = tile.getNeighborRight();
            if (neighbor) {
                this.template[col + 1][row] = neighbor.open ? Template.OPEN : Template.CLOSED;
            }
        }

        /////////////////////////////////////////
        // DIAGONAL NEIGHBORS
        /////////////////////////////////////////

        // Top Left
        if (this.template[col - 1][row - 1] === Template.IGNORE) {
            const neighbor = tile.getNeighborTopLeft();
            if (neighbor) {
                this.template[col - 1][row - 1] = neighbor.open ? Template.OPEN : Template.CLOSED;
            }
        }

        // Top Right
        if (this.template[col + 1][row - 1] === Template.IGNORE) {
            const neighbor = tile.getNeighborTopRight();
            if (neighbor) {
                this.template[col + 1][row - 1] = neighbor.open ? Template.OPEN : Template.CLOSED;
            }
        }

        // Bottom Left
        if (this.template[col - 1][row - 1] === Template.IGNORE) {
            const neighbor = tile.getNeighborBottomLeft();
            if (neighbor) {
                this.template[col - 1][row - 1] = neighbor.open ? Template.OPEN : Template.CLOSED;
            }
        }

        // Bottom Right
        if (this.template[col + 1][row - 1] === Template.IGNORE) {
            const neighbor = tile.getNeighborBottomRight();
            if (neighbor) {
                this.template[col + 1][row - 1] = neighbor.open ? Template.OPEN : Template.CLOSED;
            }
        }
    }

    /**
     * @private
     * @param {GridTile} tile 
     * @param {number} col 
     * @param {number} row 
     * @param {number} pathIndex
     * @param {boolean} tileHealthNegligible
     */
    populateNegligibleTileAndNeighbors(tile, col, row, pathIndex) {

        this.template[col][row] = pathIndex;

        // Top
        if (this.template[col][row - 1] === Template.IGNORE) {
            const neighbor = tile.getNeighborTop();
            if (neighbor) {
                this.template[col][row - 1] = Template.OPEN;
            }
        }

        // Below
        if (this.template[col][row + 1] === Template.IGNORE) {
            const neighbor = tile.getNeighborBottom();
            if (neighbor) {
                this.template[col][row + 1] = Template.OPEN;
            }
        }

        // Left
        if (this.template[col - 1][row] === Template.IGNORE) {
            const neighbor = tile.getNeighborLeft();
            if (neighbor) {
                this.template[col - 1][row] = Template.OPEN;
            }
        }

        // Right
        if (this.template[col + 1][row] === Template.IGNORE) {
            const neighbor = tile.getNeighborRight();
            if (neighbor) {
                this.template[col + 1][row] = Template.OPEN;
            }
        }

        /////////////////////////////////////////
        // DIAGONAL NEIGHBORS
        /////////////////////////////////////////

        // Top Left
        if (this.template[col - 1][row - 1] === Template.IGNORE) {
            const neighbor = tile.getNeighborTopLeft();
            if (neighbor) {
                this.template[col - 1][row - 1] = Template.OPEN;
            }
        }

        // Top Right
        if (this.template[col + 1][row - 1] === Template.IGNORE) {
            const neighbor = tile.getNeighborTopRight();
            if (neighbor) {
                this.template[col + 1][row - 1] = Template.OPEN;
            }
        }

        // Bottom Left
        if (this.template[col - 1][row - 1] === Template.IGNORE) {
            const neighbor = tile.getNeighborBottomLeft();
            if (neighbor) {
                this.template[col - 1][row - 1] = Template.OPEN;
            }
        }

        // Bottom Right
        if (this.template[col + 1][row - 1] === Template.IGNORE) {
            const neighbor = tile.getNeighborBottomRight();
            if (neighbor) {
                this.template[col + 1][row - 1] = Template.OPEN;
            }
        }
    }

    // testSquare(situationText) {
    //     // test that each row is the same length
    //     let square = true;
    //     let len = this.template[0].length;
    //     for (let col = 1; col < this.template.length; col++) {
    //         const len2 = this.template[col].length;
    //         if (len !== len2) {
    //             console.log('growTemplate row length mismatch!  len=' + len + ' vs len2=' + len2);
    //             square = false;
    //         }                
    //     }

    //     if (square) {
    //         console.log('template is square.  situation=' + situationText);
    //     }
    //     else {
    //         console.log('template NOT square!!!!!!!!!! situation=' + situationText);
    //     }
    // }

    // printTemplate() {
    //     if (this.template.length === 0) {
    //         console.log('printTemplate() template empty');
    //         return;
    //     }

    //     const rows = this.template[0].length;
    //     for (let row = 0; row < rows; row++) {
    //         let s = '';
    //         for (let col = 0; col < this.template.length; col++) {
    //             const v = this.template[col][row];
    //             if (col === this.startColIndex && row === this.startRowIndex) {
    //                 s += 'S';
    //             }
    //             else if (v === Template.IGNORE) {
    //                 s += '.';
    //             }
    //             else if (v === Template.OPEN) {
    //                 s += 'o';
    //             }
    //             else if (v === Template.CLOSED) {
    //                 s += 'x';
    //             }
    //             else if (v > 0) { // <-- path 
    //                 s += v.toString();
    //             }
    //             else {
    //                 s += '?';
    //                 console.log('bad val: ' + v);
    //             }
    //         }
    //         console.log(s + ' r=' + row);
    //     }
    // }
}